import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forNamespace, forTemplate } from '@trello/legacy-i18n';
import { currentLocale, supportedLocales } from '@trello/locale';
import { Button } from '@trello/nachos/button';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Select } from '@trello/nachos/select';
import { Spinner } from '@trello/nachos/spinner';

import { Alerts } from 'app/scripts/views/lib/Alerts';
import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { PopoverConfirm } from 'app/src/components/PopoverConfirm';
import { useCreatePluginListingMutation } from './CreatePluginListingMutation.generated';
import { useDeletePluginListingMutation } from './DeletePluginListingMutation.generated';
import { InputErrors } from './InputErrors';
import type { PluginListingsQuery } from './PluginListingsQuery.generated';
import { usePluginListingsQuery } from './PluginListingsQuery.generated';
import { isStringInRange } from './pluginValidators';
import { useUpdatePluginListingMutation } from './UpdatePluginListingMutation.generated';
import { useValidatedState } from './useValidatedState';

import styles from './PowerUpListingsPage.less';

const format = forTemplate('app_management');

type Plugin = NonNullable<PluginListingsQuery['plugin']>;
type PluginListing = NonNullable<Plugin['listings']>[number];

interface Option {
  value: string;
  label: string;
}

// eslint-disable-next-line @trello/no-module-logic
const localeOptions: Option[] = supportedLocales.map((locale) => ({
  value: locale.code,
  label: locale.name,
}));

function getLocaleOptionByCode(code: string): Option | undefined {
  return localeOptions.find(
    (l) => l.value.toLowerCase() === code.toLowerCase(),
  );
}

function groupLocaleOptions(listings: PluginListing[]) {
  const supportedCodes = listings.map((listing) =>
    listing.locale.toLowerCase(),
  );

  const supportedLocalesGroup = {
    label: format('supported'),
    options: supportedCodes
      .map((code) => getLocaleOptionByCode(code))
      .filter((option): option is Option => typeof option !== 'undefined'),
  };

  const unsupportedLocalesGroup = {
    label: format('unsupported'),
    options: localeOptions.filter(
      (locale) => !supportedCodes.includes(locale.value.toLowerCase()),
    ),
  };

  return [supportedLocalesGroup, unsupportedLocalesGroup];
}

export const PowerUpListingsPage: FunctionComponent<{
  idPlugin: string;
}> = ({ idPlugin }) => {
  // eslint-disable-next-line @trello/no-apollo-refetch
  const { data, loading, error, refetch } = usePluginListingsQuery({
    variables: {
      idPlugin,
    },
    waitOn: ['None'],
  });
  const plugin = data?.plugin;
  const [activeLocale, setActiveLocale] = useState(currentLocale);
  const [pluginListingId, setPluginListingId] = useState<string | null>(null);
  const [name, setName, nameErrors] = useValidatedState(
    '',
    isStringInRange(
      1,
      50,
      format('error-must-be-characters-long', {
        min: '1',
        max: '50',
      }),
    ),
  );
  const [overview, setOverview, overviewErrors] = useValidatedState(
    '',
    isStringInRange(
      0,
      140,
      format('error-must-be-characters-long', {
        min: '0',
        max: '140',
      }),
    ),
  );
  const [description, setDescription, descriptionErrors] = useValidatedState(
    '',
    isStringInRange(
      0,
      16384,
      format('error-must-be-characters-long', {
        min: '0',
        max: '16384',
      }),
    ),
  );
  const { triggerRef, toggle, popoverProps, hide } =
    usePopover<HTMLButtonElement>();

  useEffect(() => {
    const activeListing = plugin?.listings?.find(
      (l) => l.locale.toLowerCase() === activeLocale.toLowerCase(),
    );
    if (activeListing) {
      setPluginListingId(activeListing.id || null);
      // @ts-expect-error
      setName(activeListing.name);
      // @ts-expect-error
      setOverview(activeListing.overview);
      // @ts-expect-error
      setDescription(activeListing.description);
    } else {
      setPluginListingId(null);
      setName('');
      setOverview('');
      setDescription('');
    }
  }, [
    activeLocale,
    setPluginListingId,
    setName,
    setOverview,
    setDescription,
    plugin?.listings,
  ]);

  const [createPluginListing, { loading: creating }] =
    useCreatePluginListingMutation();
  const [updatePluginListing, { loading: updating }] =
    useUpdatePluginListingMutation();
  const [deletePluginListing, { loading: deleting }] =
    useDeletePluginListingMutation();

  const upsertPluginListing = useCallback(async () => {
    const traceId = Analytics.startTask({
      taskName: 'edit-plugin/listings',
      source: 'powerUpAdminListingsScreen',
      attributes: {
        idPlugin,
      },
    });
    try {
      if (pluginListingId) {
        await updatePluginListing({
          variables: {
            idPlugin,
            pluginListingId,
            name,
            description,
            overview,
          },
        });
      } else {
        await createPluginListing({
          variables: {
            idPlugin,
            locale: activeLocale,
            name,
            description,
            overview,
          },
        });
      }
      Analytics.taskSucceeded({
        taskName: 'edit-plugin/listings',
        traceId,
        source: 'powerUpAdminListingsScreen',
        attributes: {
          idPlugin,
        },
      });
      refetch();
      Alerts.show('changes-saved', 'info', 'changessaved', 3000);
      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error) {
      Analytics.taskFailed({
        taskName: 'edit-plugin/listings',
        traceId,
        source: 'powerUpAdminListingsScreen',
        attributes: {
          idPlugin,
        },
        error,
      });
    }
  }, [
    pluginListingId,
    updatePluginListing,
    idPlugin,
    name,
    description,
    overview,
    refetch,
    createPluginListing,
    activeLocale,
  ]);

  const deleteCurrentPluginListing = useCallback(async () => {
    if (pluginListingId) {
      hide();
      await deletePluginListing({
        variables: {
          idPlugin,
          pluginListingId,
        },
      });
    }
    refetch();
    Alerts.show(
      'power-up-listing-deleted',
      'info',
      'poweruplistingdeleted',
      3000,
    );
  }, [pluginListingId, refetch, deletePluginListing, idPlugin, hide]);

  const selectListingOnChange = useCallback(
    ({ value }: Option) => setActiveLocale(value),
    [setActiveLocale],
  );

  const formatConfirm = forNamespace('confirm');

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !plugin) {
    return <div>{format('something-went-wrong')}</div>;
  }

  const buttonIsLoading = creating || updating;

  const canUpdate =
    !buttonIsLoading &&
    name.length &&
    !nameErrors &&
    !descriptionErrors &&
    !overviewErrors;

  const canDelete =
    name.length &&
    !nameErrors &&
    !descriptionErrors &&
    !overviewErrors &&
    plugin?.listings &&
    plugin.listings.length > 1 &&
    plugin.listings?.some(
      (el) => el.locale.toLowerCase() === activeLocale.toLowerCase(),
    );

  return (
    <>
      <div className={styles.header}>
        <h1>{format('listings')}</h1>
      </div>

      <div className={styles.formSection}>
        <div>
          <label>{format('language')}</label>
          <Select
            blurInputOnSelect={true}
            className={styles.pluginInput}
            options={groupLocaleOptions(plugin.listings || [])}
            value={getLocaleOptionByCode(activeLocale)}
            // @ts-expect-error
            onChange={selectListingOnChange}
            isSearchable
          />
        </div>
        <div className={styles.inputDescription}>
          {format('language-description')}
        </div>
      </div>

      <div className={styles.formSection}>
        <label>
          {format('name')}
          <input
            type="text"
            className={classNames({
              [styles.pluginInput]: true,
              'input-error': name.length > 0 && nameErrors,
            })}
            name="name"
            placeholder={format('power-up-name')}
            value={name}
            // @ts-expect-error

            onChange={(e) => setName(e.target.value)}
          />
          {name.length ? <InputErrors errors={nameErrors} /> : null}
        </label>
        <div className={styles.inputDescription}>
          {format('name-description')}
        </div>
      </div>

      <div className={styles.formSection}>
        <label>
          {format('overview')}
          <input
            type="text"
            className={classNames({
              [styles.pluginInput]: true,
              'input-error': overviewErrors,
            })}
            name="overview"
            placeholder={format('overview')}
            value={overview}
            // @ts-expect-error

            onChange={(e) => setOverview(e.target.value)}
          />
          <InputErrors errors={overviewErrors} />
        </label>
        <div className={styles.inputDescription}>
          {format('overview-description')}
        </div>
      </div>

      <div className={styles.formSection}>
        <label>
          {format('description')}
          <textarea
            className={classNames({
              [styles.pluginInput]: true,
              [styles.descriptionInput]: true,
              'input-error': descriptionErrors,
            })}
            name="description"
            placeholder={format('description')}
            value={description}
            // @ts-expect-error

            onChange={(e) => setDescription(e.target.value)}
          />
          <InputErrors errors={descriptionErrors} />
        </label>
        <div
          className={styles.inputDescription}
          // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{
            __html: format('description-description'),
          }}
        />
      </div>
      <div className={classNames(styles.footer, styles.formFooter)}>
        <Button
          appearance="danger"
          ref={triggerRef}
          onClick={toggle}
          isDisabled={!canDelete}
        >
          {deleting ? <Spinner small /> : format('delete')}
        </Button>
        <Popover
          {...popoverProps}
          title={formatConfirm(['delete plugin listing', 'title'])}
        >
          <PopoverConfirm
            isDanger
            confirmKey={'delete plugin listing'}
            confirmCallback={deleteCurrentPluginListing}
          />
        </Popover>
        <Button
          appearance="primary"
          onClick={upsertPluginListing}
          isDisabled={!canUpdate}
        >
          {buttonIsLoading ? <Spinner small /> : format('save')}
        </Button>
      </div>
    </>
  );
};
